import { useEffect, useRef } from "react"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { selectActiveFilters } from "../redux/global-map.slice"
import { fetchFacets } from "../redux/global-map.thunks"
import type { GlobalMapFilters } from "../redux/global-map.types"

/**
 * Keeps `state.globalMap.facets` in sync with the committed filters —
 * mount fetch plus a refetch on every real filters change, mirroring
 * `useMapFeatures`'s own filters effect. This is the wiring Task 16/18's
 * reports flagged as real but explicitly out of their own scope ("nothing
 * dispatches `fetchFacets` anywhere yet"), confirmed still missing by Task
 * 24's manual QA: the salesperson/project manager/team member/subcontractor/
 * status/project type pickers all rendered "No data" against a live backend
 * that genuinely had real facet rows to serve.
 *
 * Deliberately its own hook rather than folded into `useMapFeatures` —
 * facets take no `viewport` and no free-text `search` (see `fetchFacets`'s
 * own comment in `global-map.thunks.ts`), so there is no shared trigger
 * between the two beyond `filters` itself, and mixing the two concerns into
 * one hook would make `useMapFeatures`'s already-large effect surface harder
 * to reason about for no real benefit.
 *
 * `ready` (defaults `true` for a caller that doesn't pass it, e.g. this
 * hook's own standalone tests) gates the fetch on `useGlobalMapUrlSync`'s
 * mount-time URL read having completed — without it, a URL carrying filter
 * params fires this fetch once against the pre-read (default) filters and
 * again once the real ones land (Finding 2, Increment 3 final whole-branch
 * review). `lastFetchedFiltersRef` (a value comparison, not a "skip the
 * first run" flag) is what keeps a `ready` flip and a URL-synced `filters`
 * change landing in the same commit from firing twice.
 */
export function useGlobalMapFacets(ready: boolean = true): void {
  const dispatch = useAppDispatch()
  const filters = useAppSelector(selectActiveFilters)
  const lastFetchedFiltersRef = useRef<GlobalMapFilters | null>(null)

  // `filters` is a new object reference on every `setFilters`/`clearFilters`
  // dispatch (and only then — see `useMapFeatures`'s identical reasoning for
  // its own filters effect), so this both fetches once `ready` (the current
  // render's `filters`, by then the real URL-synced ones) and refetches
  // exactly on a real later filter change.
  useEffect(() => {
    if (!ready) return
    if (filters === lastFetchedFiltersRef.current) return
    lastFetchedFiltersRef.current = filters

    dispatch(fetchFacets(filters))
  }, [ready, filters, dispatch])
}

export default useGlobalMapFacets
